import React, {
  createContext,
  useContext,
  ReactNode,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { useConfig } from "../hooks/useConfig";
import { setApiBaseUrl } from "../services/api";

// Dynamic config interface - can contain any FE_* variables
interface AppConfig {
  FE_API_URL?: string;
  FE_ENVIRONMENT?: string;
  FE_APP_NAME?: string;
  FE_VERSION?: string;
  [key: string]: string | undefined; // Allow any additional FE_* variables
}

interface ConfigContextType {
  config: AppConfig;
  loading: boolean;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const ConfigProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { config, loading } = useConfig();
  const apiUrlRef = useRef<string | null>(null);

  // Update API base URL when config changes (only once)
  useEffect(() => {
    if (
      !loading &&
      config.FE_API_URL &&
      apiUrlRef.current !== config.FE_API_URL
    ) {
      apiUrlRef.current = config.FE_API_URL;
      setApiBaseUrl(config.FE_API_URL);
    }
  }, [config.FE_API_URL, loading]);

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue = useMemo(
    () => ({
      config,
      loading,
    }),
    [config, loading]
  );

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
      </div>
    );
  }

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useAppConfig = (): ConfigContextType => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error("useAppConfig must be used within a ConfigProvider");
  }
  return context;
};
